/**
 * Online: a thin client over the WebSocket. The server owns the game; this
 * file owns the connection, the reconnect token, and the protocol handshake.
 */

import type { GameEvent, SeatActionBody } from '@shared/engine';
import { ERROR_TEXT, PROTO, type ClientMsg, type RoomInfo, type ServerMsg } from '@shared/protocol';
import type { View } from '@shared/view';
import { type ConnStatus, Emitter, type GameAdapter } from './adapter';

const TOKEN_KEY = 'cribbage.online.token.v1';
const RELOAD_GUARD = 'cribbage.proto.reloaded';

function loadToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}
function saveToken(t: string | null) {
  try {
    if (t) localStorage.setItem(TOKEN_KEY, t);
    else localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* private mode */
  }
}

export type Intent = { kind: 'create'; name: string } | { kind: 'join'; code: string; name: string } | { kind: 'resume' };

export class OnlineGame implements GameAdapter {
  readonly mode = 'online' as const;
  private sock: WebSocket | null = null;
  private view: View | null = null;
  private room: RoomInfo | null = null;
  private views = new Emitter<[View, GameEvent[]]>();
  private statusE = new Emitter<[ConnStatus]>();
  private roomE = new Emitter<[RoomInfo | null]>();
  private errors = new Emitter<[string, string]>();
  private status: ConnStatus = { k: 'connecting' };
  private attempt = 0;
  private closed = false;
  private intent: Intent;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(intent: Intent) {
    this.intent = intent;
    this.connect();
  }

  private setStatus(s: ConnStatus) {
    this.status = s;
    this.statusE.emit(s);
  }

  private url(): string {
    const proto = location.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${proto}//${location.host}/ws`;
  }

  private connect() {
    if (this.closed) return;
    this.setStatus(this.attempt === 0 ? { k: 'connecting' } : { k: 'reconnecting', attempt: this.attempt });
    const ws = new WebSocket(this.url());
    this.sock = ws;
    ws.onopen = () => {
      const token = this.intent.kind === 'resume' || this.room ? loadToken() : null;
      this.send({ t: 'hello', proto: PROTO, token: token ?? undefined });
    };
    ws.onmessage = (ev) => {
      let msg: ServerMsg;
      try {
        msg = JSON.parse(String(ev.data));
      } catch {
        return;
      }
      this.handle(msg);
    };
    ws.onclose = (ev) => {
      if (this.sock !== ws) return;
      this.sock = null;
      if (this.closed) return;
      if (ev.code === 4001 || ev.code === 4002 || ev.code === 4003) {
        this.setStatus({ k: 'closed', reason: ev.code === 4002 ? 'You opened this table on another device.' : ev.code === 4003 ? ERROR_TEXT.TRY_LATER : ERROR_TEXT.PROTO_MISMATCH });
        return;
      }
      // Reconnect with backoff: 0.5s, 1s, 2s, 4s, 8s, then every 10s.
      this.attempt += 1;
      const delay = Math.min(10_000, 500 * 2 ** Math.min(this.attempt - 1, 4));
      this.reconnectTimer = setTimeout(() => this.connect(), delay);
    };
    ws.onerror = () => {
      /* onclose follows */
    };
  }

  private send(msg: ClientMsg) {
    if (this.sock && this.sock.readyState === WebSocket.OPEN) this.sock.send(JSON.stringify(msg));
  }

  private handle(msg: ServerMsg) {
    switch (msg.t) {
      case 'welcome':
        this.attempt = 0;
        try {
          sessionStorage.removeItem(RELOAD_GUARD);
        } catch {
          /* ignore */
        }
        saveToken(msg.token);
        this.room = msg.room;
        this.view = msg.view;
        this.setStatus({ k: 'open' });
        this.roomE.emit(this.room);
        if (this.view) this.views.emit(this.view, []);
        return;
      case 'view':
        this.view = msg.view;
        this.room = msg.room;
        this.roomE.emit(this.room);
        this.views.emit(msg.view, msg.events);
        return;
      case 'room':
        this.room = msg.room;
        this.roomE.emit(this.room);
        return;
      case 'error':
        if (msg.code === 'NOT_IN_ROOM' && this.status.k !== 'open') {
          this.attempt = 0;
          if (this.room) {
            // We were at a table and the server no longer knows our seat:
            // the table is gone. Never create a new one mid-game by accident.
            saveToken(null);
            this.setStatus({ k: 'closed', reason: 'That table is gone.' });
            return;
          }
          // Fresh connection with no (valid) token: carry out the intent.
          this.setStatus({ k: 'open' });
          if (this.intent.kind === 'create') this.send({ t: 'create', name: this.intent.name });
          else if (this.intent.kind === 'join') this.send({ t: 'join', code: this.intent.code, name: this.intent.name });
          else {
            saveToken(null);
            this.errors.emit('NO_ROOM', 'That table is gone.');
            this.setStatus({ k: 'closed', reason: 'That table is gone.' });
          }
          return;
        }
        if (msg.code === 'PROTO_MISMATCH') {
          this.reloadOnce();
          return;
        }
        if (msg.code === 'NO_ROOM' && this.intent.kind === 'join') {
          this.errors.emit(msg.code, msg.message);
          this.setStatus({ k: 'closed', reason: msg.message });
          return;
        }
        this.errors.emit(msg.code, msg.message);
        return;
      case 'ended':
        saveToken(null);
        this.setStatus({ k: 'closed', reason: msg.reason === 'left' ? 'Your opponent left the table.' : 'The table was closed.' });
        return;
    }
  }

  private reloadOnce() {
    try {
      if (sessionStorage.getItem(RELOAD_GUARD)) return;
      sessionStorage.setItem(RELOAD_GUARD, '1');
    } catch {
      /* ignore */
    }
    navigator.serviceWorker?.getRegistrations().then((rs) => rs.forEach((r) => r.update())).finally(() => location.reload());
  }

  dispatch(body: SeatActionBody) {
    if (!this.view) return;
    this.send({ t: 'act', seq: this.view.seq, action: body });
  }
  onView(cb: (view: View, events: GameEvent[]) => void) {
    return this.views.on(cb);
  }
  onStatus(cb: (s: ConnStatus) => void) {
    queueMicrotask(() => cb(this.status));
    return this.statusE.on(cb);
  }
  onRoom(cb: (room: RoomInfo | null) => void) {
    queueMicrotask(() => cb(this.room));
    return this.roomE.on(cb);
  }
  onError(cb: (code: string, message: string) => void) {
    return this.errors.on(cb);
  }
  current() {
    return this.view;
  }
  rematch() {
    this.send({ t: 'rematch' });
  }
  endAbandoned() {
    this.send({ t: 'end' });
  }
  leave() {
    this.send({ t: 'leave' });
    saveToken(null);
    this.close();
  }
  newGame() {
    this.rematch();
  }
  close() {
    this.closed = true;
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    const s = this.sock;
    this.sock = null;
    try {
      s?.close(1000, 'bye');
    } catch {
      /* ignore */
    }
  }

  static hasResumableToken(): boolean {
    return !!loadToken();
  }
}
